import * as React from "react";
import { Flashbar, FlashbarProps } from "@amzn/awsui-components-react";

export interface OperationInProgressNotificationProps {
    /** Counter that increments each time a duplicate operation is attempted. */
    triggerCount: number;
}

/**
 * A brief, non-blocking notification shown when the user attempts to
 * trigger the same operation that is already in progress (e.g., clicking
 * "Delete Node" twice on the same node).
 *
 * Uses Cloudscape Flashbar for consistent theming and accessibility.
 * Does NOT block other different operations — only prevents duplicates.
 * Auto-dismisses after 2.5 seconds.
 */
export const OperationInProgressNotification: React.FC<OperationInProgressNotificationProps> = ({ triggerCount }) => {
    const [items, setItems] = React.useState<FlashbarProps.MessageDefinition[]>([]);

    React.useEffect(() => {
        if (triggerCount > 0) {
            const id = `dedup-${triggerCount}`;
            setItems([{
                type: "info",
                dismissible: true,
                dismissLabel: "Dismiss",
                onDismiss: () => setItems([]),
                content: "This operation is already in progress. Please wait for it to complete.",
                id
            }]);
            const timer = setTimeout(() => setItems([]), 2500);
            return () => clearTimeout(timer);
        }
    }, [triggerCount]);

    if (items.length === 0) return null;

    return <Flashbar items={items} />;
};
